import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, DollarSign, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { RateProfile, RateProfileHistoryEntry } from "@shyne/shared/api";
import { DailyWorkRateProfileRepository, type RatePayee } from "@/repositories/daily-work-rate-profile.repository";

type RateService = RateProfile["service"];

interface DailyWorkHourlyRateCardProps {
  payer: string;
  payee: RatePayee;
  services: RateService[];
  testIdPrefix?: string;
}

const RATE_PAGE_SIZE_OPTIONS = [5, 10, 25];
const LOG_PAGE_SIZE_OPTIONS = [5, 10, 25];

interface PaginationControlsProps {
  label: string;
  totalItems: number;
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  testId?: string;
}

function displayRate(rate: number | null | undefined): string {
  return rate == null ? "" : Number(rate).toFixed(2);
}

function logRate(rate: number | null): string {
  return rate == null ? "unset" : `$${Number(rate).toFixed(2)}`;
}

function pageCount(totalItems: number, pageSize: number): number {
  return Math.max(1, Math.ceil(totalItems / pageSize));
}

function pageRange<T>(items: T[], page: number, pageSize: number): T[] {
  return items.slice((page - 1) * pageSize, page * pageSize);
}

function PaginationControls({
  label,
  totalItems,
  page,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  testId,
}: PaginationControlsProps) {
  const totalPages = pageCount(totalItems, pageSize);
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(totalItems, page * pageSize);

  return (
    <div className="flex flex-col gap-2 border-t px-3 py-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
      <div data-testid={testId ? `${testId}-summary` : undefined}>
        {start}-{end} of {totalItems} {label}
      </div>
      <div className="flex items-center gap-2">
        <span>Rows</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => {
            onPageSizeChange(Number(value));
            onPageChange(1);
          }}
        >
          <SelectTrigger
            size="sm"
            className="h-8 w-[74px]"
            aria-label={`${label} rows per page`}
            data-testid={testId ? `${testId}-page-size` : undefined}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={`Previous ${label} page`}
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          data-testid={testId ? `${testId}-prev` : undefined}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-12 text-center">
          {page}/{totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={`Next ${label} page`}
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          data-testid={testId ? `${testId}-next` : undefined}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function DailyWorkHourlyRateCard({ payer, payee, services, testIdPrefix }: DailyWorkHourlyRateCardProps) {
  const [profiles, setProfiles] = useState<RateProfile[]>([]);
  const [logs, setLogs] = useState<RateProfileHistoryEntry[]>([]);
  const [rateServices, setRateServices] = useState<RateService[]>(services);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingServiceId, setSavingServiceId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [servicePage, setServicePage] = useState(1);
  const [servicePageSize, setServicePageSize] = useState(RATE_PAGE_SIZE_OPTIONS[0]);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(LOG_PAGE_SIZE_OPTIONS[0]);
  const servicesKey = services.map((service) => service.id).join("|");
  const servicesRef = useRef({ key: servicesKey, services });
  const worker = "worker" in payee ? payee.worker : null;
  const subcontractor = "subcontractor" in payee ? payee.subcontractor : null;

  if (servicesRef.current.key !== servicesKey) {
    servicesRef.current = { key: servicesKey, services };
  }

  const profilesByService = useMemo(
    () => new Map(profiles.map((profile) => [profile.service.id, profile])),
    [profiles]
  );
  const serviceTotalPages = pageCount(rateServices.length, servicePageSize);
  const logTotalPages = pageCount(logs.length, logPageSize);
  const visibleRateServices = useMemo(
    () => pageRange(rateServices, servicePage, servicePageSize),
    [rateServices, servicePage, servicePageSize]
  );
  const visibleLogs = useMemo(() => pageRange(logs, logPage, logPageSize), [logs, logPage, logPageSize]);

  const loadRates = useCallback(async () => {
    if (!payer || !(worker ?? subcontractor)) return;
    const subject = { payer, payee: worker ? { worker } : { subcontractor } };
    setLoading(true);
    try {
      const [nextProfiles, nextLogs, discoveredServices] = await Promise.all([
        DailyWorkRateProfileRepository.listProfiles(subject),
        DailyWorkRateProfileRepository.listHistory(subject),
        worker ? DailyWorkRateProfileRepository.listServicesForWorker(payer, worker) : Promise.resolve([] as RateService[]),
      ]);

      const nextProfilesByService = new Map(nextProfiles.map((profile) => [profile.service.id, profile]));
      const nextServices = new Map(servicesRef.current.services.map((service) => [service.id, service]));
      for (const service of [...discoveredServices, ...nextProfiles.map((profile) => profile.service)]) {
        nextServices.set(service.id, service);
      }
      const sortedServices = Array.from(nextServices.values()).sort((a, b) => a.name.localeCompare(b.name));
      const nextValues = sortedServices.reduce<Record<string, string>>((acc, service) => {
        acc[service.id] = displayRate(nextProfilesByService.get(service.id)?.hourlyRate);
        return acc;
      }, {});

      setRateServices(sortedServices);
      setProfiles(nextProfiles);
      setLogs(nextLogs);
      setValues(nextValues);
      setErrors({});
    } catch (error: any) {
      toast.error(error.message ?? "Failed to load Daily Work hourly rates");
    } finally {
      setLoading(false);
    }
  }, [payer, worker, subcontractor]);

  useEffect(() => {
    void loadRates();
  }, [loadRates, servicesKey]);

  useEffect(() => {
    setServicePage((current) => Math.min(current, serviceTotalPages));
  }, [serviceTotalPages]);

  useEffect(() => {
    setLogPage((current) => Math.min(current, logTotalPages));
  }, [logTotalPages]);

  const handleSave = async (serviceId: string) => {
    const rawValue = values[serviceId]?.trim() ?? "";
    const hourlyRate = rawValue === "" ? null : Number(rawValue);

    if (rawValue !== "" && (!Number.isFinite(hourlyRate) || hourlyRate < 0)) {
      setErrors((current) => ({ ...current, [serviceId]: "Enter a non-negative hourly rate." }));
      return;
    }

    setSavingServiceId(serviceId);
    try {
      await DailyWorkRateProfileRepository.saveRate({ payer, payee, service: serviceId, hourlyRate });
      toast.success("Daily Work hourly rate updated");
      await loadRates();
    } catch (error: any) {
      toast.error(error.message ?? "Failed to update Daily Work hourly rate");
    } finally {
      setSavingServiceId(null);
    }
  };

  return (
    <Card data-testid={testIdPrefix ? `${testIdPrefix}-card` : undefined}>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Daily Work Rates
            </CardTitle>
            <CardDescription>Hourly rates are copied into Daily Work assignments by service.</CardDescription>
          </div>
          {!loading && (
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <Badge variant="outline">{rateServices.length} services</Badge>
              <Badge variant="outline">{logs.length} changes</Badge>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading rates...
          </div>
        ) : rateServices.length === 0 ? (
          <p className="text-sm text-muted-foreground">Assign services before setting Daily Work rates.</p>
        ) : (
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-full">Service</TableHead>
                  <TableHead className="min-w-[140px]">Current</TableHead>
                  <TableHead className="min-w-[150px]">Hourly rate</TableHead>
                  <TableHead className="w-[96px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRateServices.map((service) => {
                  const profile = profilesByService.get(service.id);
                  const currentValue = displayRate(profile?.hourlyRate);
                  const value = values[service.id] ?? "";
                  const isDirty = value !== currentValue;
                  const inputId = testIdPrefix ? `${testIdPrefix}-input-${service.id}` : `daily-work-rate-${service.id}`;

                  return (
                    <TableRow key={service.id} className="align-top">
                      <TableCell className="min-w-[220px] whitespace-normal font-medium">
                        <Label htmlFor={inputId} className="leading-5">
                          {service.name}
                        </Label>
                        {errors[service.id] && <p className="mt-1 text-xs font-normal text-destructive">{errors[service.id]}</p>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {profile?.hourlyRate == null ? "Unset" : logRate(profile.hourlyRate)}
                      </TableCell>
                      <TableCell>
                        <Input
                          id={inputId}
                          className="h-8"
                          aria-label={`${service.name} hourly rate`}
                          data-testid={testIdPrefix ? `${testIdPrefix}-input-${service.id}` : undefined}
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          placeholder="Unset"
                          value={value}
                          onChange={(event) => {
                            setValues((current) => ({ ...current, [service.id]: event.target.value }));
                            setErrors((current) => ({ ...current, [service.id]: null }));
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSave(service.id)}
                          disabled={savingServiceId === service.id || !isDirty}
                          data-testid={testIdPrefix ? `${testIdPrefix}-save-${service.id}` : undefined}
                        >
                          {savingServiceId === service.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          Save
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <PaginationControls
              label="services"
              totalItems={rateServices.length}
              page={servicePage}
              pageSize={servicePageSize}
              pageSizeOptions={RATE_PAGE_SIZE_OPTIONS}
              onPageChange={setServicePage}
              onPageSizeChange={setServicePageSize}
              testId={testIdPrefix ? `${testIdPrefix}-services-pagination` : undefined}
            />
          </div>
        )}

        <div className="overflow-hidden rounded-md border" data-testid={testIdPrefix ? `${testIdPrefix}-log` : undefined}>
          <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
            <h4 className="text-sm font-medium">Change Log</h4>
            {!loading && <span className="text-xs text-muted-foreground">{logs.length} total</span>}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading changes...
            </div>
          ) : logs.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No rate changes yet.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Updated by</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="max-w-[220px] overflow-hidden text-ellipsis">
                        {log.service.name ?? "Service"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {logRate(log.from)} to {logRate(log.to)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{log.author?.name ?? "System"}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(log.at), "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <PaginationControls
                label="changes"
                totalItems={logs.length}
                page={logPage}
                pageSize={logPageSize}
                pageSizeOptions={LOG_PAGE_SIZE_OPTIONS}
                onPageChange={setLogPage}
                onPageSizeChange={setLogPageSize}
                testId={testIdPrefix ? `${testIdPrefix}-log-pagination` : undefined}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
